// savePending.service.ts

/* global Word */

import {
  CITATION_BOOKMARK_NS,
  escapeXmlAttr,
  parseCitationBookmarkXml,
  SavedMatch,
} from "../lib/scanner";
import {
  AnalyzeResult,
  CitationType,
  ResolutionStatus,
  ResolvedLawCase,
} from "../models/benchly.models";
import { wordApiColors } from "../theme/benchly-theme";

const CITATION_HYPERLINK_COLOR = wordApiColors.hyperlinkBlue;

interface SavePendingResult {
  updatedCitations: AnalyzeResult[];
  removedIds: string[];
}

/**
 * Saves pending citation changes to the Word document.
 *
 * For each citation with a resolvedLawCase:
 * - If resolvedLawCase is "none": Remove the bookmark and delete from storage tracking
 * - If resolvedLawCase is a ResolvedLawCase:
 *    - SingleCite: update citation text (and title if safely found), apply hyperlink, set status to Certified
 *    - ParallelCites: DO NOT update text/title; apply hyperlink to the bookmarked span only; set status to Certified
 *
 * @param pendingCitations - Array of citations with pending changes (resolvedLawCase defined)
 * @returns Object containing updated citations and removed bookmark IDs
 */
export async function savePendingCitations(
  pendingCitations: AnalyzeResult[]
): Promise<SavePendingResult> {
  const updatedCitations: AnalyzeResult[] = [];
  const removedIds: string[] = [];

  if (pendingCitations.length === 0) {
    return { updatedCitations, removedIds };
  }

  await Word.run(async (context) => {
    const ignoredCitations = pendingCitations.filter((c) => c.resolvedLawCase === "none");
    const resolvedCitations = pendingCitations.filter(
      (c) => c.resolvedLawCase !== "none" && c.resolvedLawCase !== undefined
    );

    console.log(
      `📍 Processing ${resolvedCitations.length} resolved and ${ignoredCitations.length} ignored citations`
    );

    // ─────────────────────────────────────────────────────────────
    // Phase 1: Handle ignored citations - remove bookmarks and formatting
    // ─────────────────────────────────────────────────────────────
    for (const citation of ignoredCitations) {
      try {
        const bookmarkName = citation.documentReferenceId;
        if (!bookmarkName) {
          console.warn(`Invalid bookmark name: ${citation.documentReferenceId}`);
          continue;
        }

        const range = context.document.getBookmarkRangeOrNullObject(bookmarkName);
        range.load("isNullObject");
        await context.sync();

        if (!range.isNullObject) {
          range.font.color = wordApiColors.textPrimary;
          range.font.underline = Word.UnderlineType.none;
          (range as any).hyperlink = "";

          context.document.deleteBookmark(bookmarkName);

          console.log(
            `🗑️ Removed bookmark for ignored citation: ${citation.citations?.[0]?.identifierOnDocument || "unknown"}`
          );
        }

        removedIds.push(bookmarkName);
      } catch (error) {
        console.error(`Error removing bookmark for ${citation.documentReferenceId}:`, error);
        const bookmarkName = citation.documentReferenceId;
        if (bookmarkName) removedIds.push(bookmarkName);
      }
    }

    await context.sync();

    // Track bookmark metadata updates for Phase 3
    const bookmarkUpdates = new Map<string, { newText?: string; newTitle?: string }>();

    // ─────────────────────────────────────────────────────────────
    // Phase 2: Handle resolved citations
    // - ParallelCites: hyperlink only
    // - SingleCite: update citation text/title (legacy behavior) + hyperlink
    // ─────────────────────────────────────────────────────────────
    for (const citation of resolvedCitations) {
      try {
        const resolvedCase = citation.resolvedLawCase as ResolvedLawCase;
        const bookmarkName = citation.documentReferenceId;

        if (!bookmarkName) {
          console.warn(`Invalid bookmark name: ${citation.documentReferenceId}`);
          continue;
        }

        const range = context.document.getBookmarkRangeOrNullObject(bookmarkName);
        range.load("isNullObject,text");
        await context.sync();

        if (range.isNullObject) {
          console.warn(`Bookmark not found: ${bookmarkName}`);
          continue;
        }

        const currentText = range.text || "";

        // Track the active range — may be replaced if text is updated (SingleCite only)
        let activeRange: Word.Range = range;

        const isParallel = citation.citationType === CitationType.ParallelCites;

        if (!isParallel) {
          // ─────────────────────────────────────────────
          // SingleCite: restore old behavior (update text/title)
          // ─────────────────────────────────────────────

          // Prefer canonical identifier from matched doc; fallback to resolvedCase identifierOnDocument.
          const newIdentifier =
            resolvedCase.foundDocs?.[0]?.identifier ?? resolvedCase.identifierOnDocument ?? "";

          const oldIdentifier = citation.citations?.[0]?.identifierOnDocument ?? currentText;

          if (newIdentifier && newIdentifier.trim() && newIdentifier !== oldIdentifier) {
            activeRange = range.insertText(newIdentifier, Word.InsertLocation.replace);
            activeRange.insertBookmark(bookmarkName);
            console.log(`📝 Updated citation text: "${currentText}" → "${newIdentifier}"`);
          }

          // Title replacement (only if we can safely find a single match in the paragraph)
          const newTitle = resolvedCase.foundDocs?.[0]?.title ?? resolvedCase.titleOnDocument ?? "";

          const oldTitle = citation.citations?.[0]?.titleOnDocument ?? "";

          if (newTitle && oldTitle && newTitle !== oldTitle && oldTitle.trim().length > 0) {
            const paragraph = activeRange.paragraphs.getFirst();
            paragraph.load("text");
            await context.sync();

            const searchResults = paragraph.search(oldTitle, {
              matchCase: true,
              matchWholeWord: false,
            });
            searchResults.load("items");
            await context.sync();

            if (searchResults.items.length === 0) {
              console.warn(`⚠️ Old title not found in paragraph: "${oldTitle}"`);
            } else if (searchResults.items.length > 1) {
              console.warn(
                `⚠️ Multiple matches (${searchResults.items.length}) found for title "${oldTitle}". Skipping replacement to avoid ambiguity.`
              );
            } else {
              const titleRange = searchResults.items[0];

              const comparison = activeRange.compareLocationWith(titleRange);
              await context.sync();

              // Expected layout: citation appears before title in the paragraph.
              if (comparison.value === "Before" || comparison.value === "AdjacentBefore") {
                const newTitleRange = titleRange.insertText(newTitle, Word.InsertLocation.replace);
                newTitleRange.font.italic = true;
                console.log(`📝 Updated title in document: "${oldTitle}" → "${newTitle}" (italic)`);
              } else {
                console.warn(
                  `⚠️ Title "${oldTitle}" found but in unexpected position (${comparison.value}). Skipping replacement.`
                );
              }
            }
          }
        } else {
          // ParallelCites: do not alter text/title. Only hyperlink the bookmarked span.
          // (This prevents breaking the structured list of reporter cites.)
        }

        // ─────────────────────────────────────────────
        // Hyperlink application (applies to BOTH)
        // - ParallelCites: hyperlink original bookmarked span
        // - SingleCite: hyperlink updated span (activeRange)
        // ─────────────────────────────────────────────
        const hyperlink = resolvedCase.foundDocs?.[0]?.hyperlink;
        if (hyperlink) {
          (activeRange as any).hyperlink = hyperlink;
          activeRange.font.color = CITATION_HYPERLINK_COLOR;
          activeRange.font.underline = Word.UnderlineType.single;
          console.log(`🔗 Applied hyperlink to citation: "${(activeRange.text ?? "").trim()}"`);
        }

        // Track bookmark metadata updates:
        // - Parallel: preserve original identifier/title
        // - Single: store the new identifier/title if we updated them (or can infer them)
        const originalIdentifier = citation.citations?.[0]?.identifierOnDocument || currentText;
        const originalTitle = citation.citations?.[0]?.titleOnDocument || "";

        // Only request metadata updates if we actually changed the document text/title.
        // - ParallelCites: do not touch entry.text/title in XML (avoid desync)
        // - SingleCite: set fields only when changed
        if (!isParallel) {
          const updates: { newText?: string; newTitle?: string } = {};

          // If we replaced citation text in the doc, activeRange is the replaced span.
          // Use activeRange.text to reflect what's actually in Word.
          const activeText = (activeRange.text ?? "").trim();
          if (activeText) {
            updates.newText = activeText;
          }

          // Only set title if we successfully replaced it (we don't have a cheap “did replace” flag),
          // so use the newTitleForMeta only if it exists and oldTitle was non-empty.
          // This matches the logic that title replacement only runs when oldTitle exists.
          const oldTitle = citation.citations?.[0]?.titleOnDocument ?? "";
          const newTitle = resolvedCase.foundDocs?.[0]?.title ?? resolvedCase.titleOnDocument ?? "";

          if (oldTitle && newTitle && newTitle !== oldTitle) {
            updates.newTitle = newTitle;
          }

          if (updates.newText !== undefined || updates.newTitle !== undefined) {
            bookmarkUpdates.set(bookmarkName, updates);
          }
        }
        // else ParallelCites: do not set bookmarkUpdates — preserve existing XML entry fields

        // Create updated citation record: keep citations array but update status
        const updatedResolved: ResolvedLawCase =
          resolvedCase && resolvedCase !== ("none" as any)
            ? { ...resolvedCase, status: ResolutionStatus.ValidatedAndCertified }
            : resolvedCase;

        const updatedCitation: AnalyzeResult = {
          ...citation,
          citations: (citation.citations ?? []).map((c) => ({
            ...c,
            status: ResolutionStatus.ValidatedAndCertified,
          })),
          resolvedLawCase: updatedResolved,
          lastUpdated: new Date().toISOString(),
        };

        updatedCitations.push(updatedCitation);
        console.log(`✅ Citation certified: ${bookmarkUpdates.get(bookmarkName)?.newText ?? ""}`);
      } catch (error) {
        console.error(`Error updating citation ${citation.documentReferenceId}:`, error);
      }
    }

    await context.sync();

    // ─────────────────────────────────────────────────────────────
    // Phase 3: Update bookmark metadata XML with new identifiers and titles
    // ─────────────────────────────────────────────────────────────
    if (bookmarkUpdates.size > 0 || removedIds.length > 0) {
      try {
        const parts = context.document.customXmlParts.getByNamespace(CITATION_BOOKMARK_NS);
        parts.load("items");
        await context.sync();

        if (parts.items.length > 0) {
          const xmlPart = parts.items[0];
          const xmlNode = xmlPart.getXml();
          await context.sync();

          const entries: SavedMatch[] = parseCitationBookmarkXml(xmlNode.value);

          const updatedEntries = entries
            .filter((entry) => !removedIds.includes(entry.bookmarkName))
            .map((entry) => {
              const update = bookmarkUpdates.get(entry.bookmarkName);
              if (!update) return entry;

              return {
                ...entry,
                text: update.newText !== undefined ? update.newText : entry.text,
                title: update.newTitle !== undefined ? update.newTitle : entry.title,
              };
            });

          xmlPart.delete();

          const xmlEntries = updatedEntries
            .map((e) => {
              const citationsJson = JSON.stringify(e.citations ?? []);
              return `<c bk="${escapeXmlAttr(e.bookmarkName)}" p="${escapeXmlAttr(e.pattern)}" t="${escapeXmlAttr(e.text)}" tl="${escapeXmlAttr(e.title)}" ct="${escapeXmlAttr(e.citationType === 1 ? "ParallelCites" : "SingleCite")}" cits="${escapeXmlAttr(citationsJson)}" id="${escapeXmlAttr(e.identifierText ?? "")}" api="${escapeXmlAttr(e.apiIdentifier ?? "")}" y="${escapeXmlAttr(e.year ?? "")}" pg="${escapeXmlAttr(e.pages ?? "")}"/>`;
            })
            .join("");

          const xml = `<?xml version="1.0" encoding="UTF-8"?><CitationBookmarks xmlns="${CITATION_BOOKMARK_NS}">${xmlEntries}</CitationBookmarks>`;
          context.document.customXmlParts.add(xml);
          await context.sync();

          console.log(
            `📝 Updated bookmark metadata XML: ${bookmarkUpdates.size} updated, ${removedIds.length} removed`
          );
        }
      } catch (error) {
        console.error("Error updating bookmark metadata XML:", error);
      }
    }
  });

  return { updatedCitations, removedIds };
}
